import React from 'react';
import { Timer, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { formatRupiah } from '../recruitment/utils/formatRupiah';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

const statusColors = {
  pending: 'bg-amber-500 text-white border border-amber-600',
  approved: 'bg-green-500 text-white border border-green-600',
  rejected: 'bg-red-500 text-white border border-red-600',
};

const statusLabels = {
  pending: 'Pending',
  approved: 'Disetujui',
  rejected: 'Ditolak',
};

const filterOptions = [
  { value: 'all', label: 'Semua Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
];

const months = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

function LoadingSkeleton({ isAdmin }) {
  return Array(5)
    .fill(0)
    .map((_, i) => (
      <tr key={i} className="border-b">
        <td className="px-4 py-3">
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden lg:table-cell">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        </td>
        {isAdmin && (
          <td className="px-4 py-3">
            <div className="h-8 w-24 bg-gray-200 rounded animate-pulse ml-auto"></div>
          </td>
        )}
      </tr>
    ));
}

function EmptyState({ isAdmin }) {
  return (
    <tr>
      <td colSpan={isAdmin ? 7 : 6} className="text-center py-12">
        <Timer className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500">Tidak ada data lembur</p>
      </td>
    </tr>
  );
}

function OvertimeRow({
  overtime,
  getEmployeeName,
  getEmployeeId,
  isAdmin,
  onEdit,
  onDelete,
  onStatusChange,
}) {
  const status = overtime?.status || 'pending';

  const formatDate = dateStr => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy', { locale: id });
    } catch {
      return dateStr;
    }
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <span className="font-semibold text-orange-700">
              {getEmployeeName(overtime.employee_id)?.charAt(0) || 'E'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate">{getEmployeeName(overtime.employee_id)}</p>
            <p className="text-sm text-gray-500">{getEmployeeId(overtime.employee_id)}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm">{formatDate(overtime.date)}</span>
      </td>
      <td className="px-4 py-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-orange-600">{overtime.duration_hours || 0}h</p>
          <p className="text-xs text-gray-600">
            {overtime.start_time} - {overtime.end_time}
          </p>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900">
          Rp {formatRupiah(overtime.total_amount || 0)}
        </p>
        <p className="text-xs text-gray-500">Rp {formatRupiah(overtime.rate_per_hour || 0)}/jam</p>
      </td>
      <td className="px-4 py-3">
        <Badge className={`${statusColors[status] || statusColors.pending} font-bold px-3 py-1`}>
          {statusLabels[status] || 'Pending'}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">
        {overtime.description || '-'}
      </td>
      {isAdmin && (
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            {status === 'pending' ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-green-600 hover:bg-green-50"
                  onClick={() => onStatusChange(overtime, 'approved')}
                  title="Setujui"
                >
                  <CheckCircle className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-600 hover:bg-red-50"
                  onClick={() => onStatusChange(overtime, 'rejected')}
                  title="Tolak"
                >
                  <XCircle className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onEdit(overtime)}
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700"
                  onClick={() => onDelete(overtime.id)}
                  title="Hapus"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}

export default function OvertimeTable({
  overtimes,
  loading,
  isAdmin,
  filterStatus,
  onFilterChange,
  selectedMonth,
  selectedYear,
  getEmployeeName,
  getEmployeeId,
  onEdit,
  onDelete,
  onAdd,
  onStatusChange,
}) {
  const monthLabel = months[selectedMonth - 1] || '';

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h3 className="font-semibold">
          Rekap Lembur - {monthLabel} {selectedYear}
        </h3>
        <CustomSelect
          value={filterStatus}
          onChange={onFilterChange}
          options={filterOptions}
          placeholder="Semua Status"
          width="w-full md:w-44"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pegawai</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Tanggal</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Lembur</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Bonus</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden lg:table-cell">
                Keterangan
              </th>
              {isAdmin && (
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Aksi</th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingSkeleton isAdmin={isAdmin} />
            ) : overtimes.length === 0 ? (
              <EmptyState isAdmin={isAdmin} />
            ) : (
              overtimes.map(overtime => (
                <OvertimeRow
                  key={overtime.id}
                  overtime={overtime}
                  getEmployeeName={getEmployeeName}
                  getEmployeeId={getEmployeeId}
                  isAdmin={isAdmin}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onStatusChange={onStatusChange}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
